import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { NS } from "./U3121";
import {
  doGetBillingSummary,
  doGetBillingSummaryInvoiceDetails,
  doGetBillingSummaryTecDetails,
  doGetBillingSummaryTimesheetDetails,
  luHandleAppiError,
  parseU3121BillingInvoiceDetailLinesFromApi,
  parseU3121BillingSummaryFromApi,
  parseU3121BillingTecDetailLinesToWipRows,
  parseU3121BillingTimesheetDetailLinesFromApi,
  type U3121BillingDocumentNature,
  type U3121BillingInvoiceDetailLine,
  type U3121BillingSummary,
  type U3121BillingTecPool,
  type U3121BillingTimesheetDetailLine,
} from "./U3121Service";
import type { U3134WorkInProgressSearchRow } from "../u3134WorkInProgress/U3134Service";

export interface U3121BillingSummaryHandlersOptions {
  onError?: (error: unknown) => void;
}

export function useU3121GetBillingSummaryHandlers(options: U3121BillingSummaryHandlersOptions = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const { onError } = options;

  const handleError = useCallback(
    (error: unknown) => {
      if (onError) {
        onError(error);
        return;
      }
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      errorResult.genericErrors?.forEach((err) => toast.error(err.text, err.title));
    },
    [onError, t, toast],
  );

  const fetch = useCallback(
    async (projectId: string): Promise<U3121BillingSummary | null> => {
      const pid = projectId?.trim();
      if (!pid) return null;
      try {
        const response = await doGetBillingSummary(axios, pid);
        return parseU3121BillingSummaryFromApi(response.data);
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError],
  );

  return { fetch };
}

export function useU3121BillingSummaryTecDetailHandlers(options: U3121BillingSummaryHandlersOptions = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const { onError } = options;

  const fetch = useCallback(
    async (projectId: string, pool: U3121BillingTecPool): Promise<U3134WorkInProgressSearchRow[]> => {
      const pid = projectId?.trim();
      if (!pid) return [];
      try {
        const response = await doGetBillingSummaryTecDetails(axios, pid, pool);
        return parseU3121BillingTecDetailLinesToWipRows(response.data);
      } catch (error: unknown) {
        if (onError) {
          onError(error);
        } else {
          const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
          errorResult.genericErrors?.forEach((err) => toast.error(err.text, err.title));
        }
        return [];
      }
    },
    [axios, onError, t, toast],
  );

  return { fetch };
}

export function useU3121BillingSummaryInvoiceDetailHandlers(options: U3121BillingSummaryHandlersOptions = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const { onError } = options;

  const fetch = useCallback(
    async (projectId: string, documentNature: U3121BillingDocumentNature): Promise<U3121BillingInvoiceDetailLine[]> => {
      const pid = projectId?.trim();
      if (!pid) return [];
      try {
        const response = await doGetBillingSummaryInvoiceDetails(axios, pid, documentNature);
        return parseU3121BillingInvoiceDetailLinesFromApi(response.data);
      } catch (error: unknown) {
        if (onError) {
          onError(error);
        } else {
          const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
          errorResult.genericErrors?.forEach((err) => toast.error(err.text, err.title));
        }
        return [];
      }
    },
    [axios, onError, t, toast],
  );

  return { fetch };
}

export function useU3121BillingSummaryTimesheetDetailHandlers(options: U3121BillingSummaryHandlersOptions = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const { onError } = options;

  const fetch = useCallback(
    async (projectId: string): Promise<U3121BillingTimesheetDetailLine[]> => {
      const pid = projectId?.trim();
      if (!pid) return [];
      try {
        const response = await doGetBillingSummaryTimesheetDetails(axios, pid);
        return parseU3121BillingTimesheetDetailLinesFromApi(response.data);
      } catch (error: unknown) {
        if (onError) {
          onError(error);
        } else {
          const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
          errorResult.genericErrors?.forEach((err) => toast.error(err.text, err.title));
        }
        return [];
      }
    },
    [axios, onError, t, toast],
  );

  return { fetch };
}
